import React from 'react';
import { DollarSign, Calculator, AlertCircle, Info, ArrowRight, ChevronDown, ChevronUp, Calendar, CheckCircle } from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import useEvents from '../hooks/useEvents';
import { addEvent } from '../lib/firebase';
import { BudgetBucket } from '../types';
import { format, parseISO, isBefore, isAfter, isSameDay } from 'date-fns';

export default function BalanceReconciliation() {
  const { user } = useAuth();
  const { events } = useEvents();
  const [bankBalance, setBankBalance] = React.useState('');
  const [selectedDate, setSelectedDate] = React.useState(format(new Date(), 'yyyy-MM-dd'));
  const [buckets, setBuckets] = React.useState<BudgetBucket[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Fetch budget buckets
  React.useEffect(() => {
    if (!user) return;

    const fetchBuckets = async () => {
      const bucketsQuery = query(
        collection(db, 'budgetBuckets'),
        where('userId', '==', user.uid)
      );
      
      const snapshot = await getDocs(bucketsQuery);
      const bucketsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as BudgetBucket[];
      
      setBuckets(bucketsList);
    };

    fetchBuckets();
  }, [user]);

  // Calculate running balance for selected date
  const calculateRunningBalance = (date: Date): number => {
    if (!events) return 0;

    // Find the latest adjustment before or on the given date
    const latestAdjustment = events
      .filter(event => 
        event.type === 'adjustment' && 
        (isBefore(event.date, date) || isSameDay(event.date, date))
      )
      .sort((a, b) => b.date.getTime() - a.date.getTime())[0];

    // If there's an adjustment, only consider transactions after it
    const startDate = latestAdjustment ? latestAdjustment.date : new Date(0);
    const startingBalance = latestAdjustment ? latestAdjustment.amount : 0;

    // Get all relevant transactions
    const relevantTransactions = events
      .filter(event => {
        const eventDate = event.date;
        return (isAfter(eventDate, startDate) || (event === latestAdjustment)) &&
               (isBefore(eventDate, date) || isSameDay(eventDate, date));
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    // Calculate running balance
    return relevantTransactions.reduce((balance, event) => {
      if (event.type === 'adjustment') {
        return event.amount;
      }
      if (event.type === 'payday') {
        return balance + event.amount;
      }
      if (event.type === 'bill' || event.type === 'bucket') {
        return balance - event.amount;
      }
      return balance;
    }, startingBalance);
  };

  // Calculate total remaining in buckets
  const totalRemaining = buckets.reduce((sum, bucket) => sum + bucket.remainingAmount, 0);

  // Get selected date's running balance
  const selectedDateBalance = calculateRunningBalance(parseISO(selectedDate));

  // Calculate the current total (buckets + running balance)
  const currentTotal = totalRemaining + selectedDateBalance;

  // Calculate the difference between bank balance and current total
  const getDifference = () => {
    if (!bankBalance) return 0;
    return currentTotal - parseFloat(bankBalance);
  };

  // Calculate the adjusted balance (selected date balance + difference)
  const getAdjustedBalance = () => {
    if (!bankBalance) return selectedDateBalance;
    return selectedDateBalance - getDifference();
  };

  const handleReconcile = async () => {
    if (!user || !bankBalance) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const adjustedBalance = getAdjustedBalance();
      
      // Create adjustment event using the adjusted balance
      await addEvent({
        id: crypto.randomUUID(),
        title: 'Balance Reconciliation',
        amount: adjustedBalance,
        type: 'adjustment',
        date: parseISO(selectedDate),
        adjustmentAmount: getDifference(),
        isPaid: true,
        paidDate: new Date()
      });

      setSuccess('Balance successfully reconciled');
      setBankBalance('');
      setIsExpanded(false);
    } catch (error) {
      console.error('Error reconciling balance:', error);
      setError('Failed to reconcile balance. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  const difference = getDifference();
  const isBalanceMatched = Math.abs(difference) < 0.01; // Using small threshold to account for floating point precision

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
      {/* Header - Always visible */}
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center">
          <Calculator className="w-5 h-5 mr-2 text-indigo-600" />
          <h2 className="text-lg font-medium text-gray-900">Balance Reconciliation</h2>
          {bankBalance && !isExpanded && (
            <span className="ml-4 text-sm text-gray-500">
              Current Entry: ${parseFloat(bankBalance).toFixed(2)}
            </span>
          )}
        </div>
        <button className="text-gray-400 hover:text-gray-600">
          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
      </div>

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="p-6 border-t border-gray-200">
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-md mb-6">
            <div className="flex">
              <Info className="h-5 w-5 text-blue-400" />
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  This tool helps you reconcile your actual bank balance with your budget.
                  Enter your current available bank balance to adjust the running balance and ensure it matches
                  your real account balance. The adjustment is calculated based on your available current bank balance
                  minus the sum of your bucket balances and running balance.
                </p>
              </div>
            </div>
          </div>

          {(error || success) && (
            <div className={`mb-4 p-4 rounded-md ${
              error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
            }`}>
              <div className="flex">
                <AlertCircle className="h-5 w-5 mr-2" />
                <p>{error || success}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="text-sm font-medium text-gray-500 mb-1">Select Date</div>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="text-sm font-medium text-gray-500 mb-1">Bucket Balance</div>
              <div className="text-2xl font-bold text-green-600">
                ${totalRemaining.toFixed(2)}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                Available in buckets
              </div>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="text-sm font-medium text-gray-500 mb-1">Running Balance</div>
              <div className={`text-2xl font-bold ${selectedDateBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                ${selectedDateBalance.toFixed(2)}
              </div>
              <div className="text-sm text-gray-500 mt-1">
                Selected date's balance
              </div>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="text-sm font-medium text-gray-500 mb-1">Current Bank Balance</div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  type="number"
                  step="0.01"
                  value={bankBalance}
                  onChange={(e) => setBankBalance(e.target.value)}
                  className="block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Current Total and Difference Display */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-sm font-medium text-gray-500 mb-1">Current Total</div>
                <div className="text-2xl font-bold text-indigo-600">
                  ${currentTotal.toFixed(2)}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  Buckets + Running Balance
                </div>
              </div>

              {bankBalance && (
                <>
                  <div>
                    <div className="text-sm font-medium text-gray-500 mb-1">Difference</div>
                    <div className={`text-2xl font-bold ${
                      isBalanceMatched ? 'text-gray-400' :
                      difference >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {difference >= 0 ? '+' : ''}{difference.toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      Amount to be adjusted
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-medium text-gray-500 mb-1">Adjusted Balance</div>
                    <div className={`text-2xl font-bold ${
                      isBalanceMatched ? 'text-gray-400' :
                      getAdjustedBalance() >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      ${getAdjustedBalance().toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      Selected date + Difference
                    </div>
                  </div>
                </>
              )}
            </div>

            {bankBalance && (
              <>
                {isBalanceMatched && (
                  <div className="mt-4 flex items-center justify-center p-3 bg-green-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                    <span className="text-green-700 font-medium">
                      Congratulations! Your account balance matches perfectly.
                    </span>
                  </div>
                )}
                
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={handleReconcile}
                    disabled={loading || isBalanceMatched}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Reconcile Balance
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}